import { Button } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { useWavesurfer } from '@wavesurfer/react'
import { Pause, Play } from 'lucide-react'
import { useEffect, useRef } from 'react'

type NectarAudioPlayerProps = {
  audioUrl: string
  onProgress?: (progress: number) => void
  onFinish?: () => void
  onError?: () => void
  onPlay?: () => void
  onPause?: () => void
  disabled?: boolean
}

export const NectarAudioPlayer = ({
  audioUrl,
  onProgress,
  onFinish,
  onError,
  onPlay,
  onPause,
  disabled
}: NectarAudioPlayerProps) => {
  const playerRef = useRef<HTMLDivElement>(null)
  const callbacksRef = useRef({ onProgress, onFinish, onError, onPlay, onPause })
  callbacksRef.current = { onProgress, onFinish, onError, onPlay, onPause }
  const { wavesurfer, isPlaying } = useWavesurfer({
    container: playerRef,
    url: audioUrl,
    cursorColor: 'transparent',
    cursorWidth: 2,
    dragToSeek: false,
    height: 40,
    waveColor: '#727272',
    progressColor: '#39c900',
    autoplay: false,
    barWidth: 3,
    barHeight: 4
  })

  useEffect(() => {
    if (!wavesurfer) return

    const handleAudioProcess = () => {
      const { onProgress } = callbacksRef.current
      if (!onProgress) return
      const dur = wavesurfer.getDuration()
      if (dur > 0) onProgress(wavesurfer.getCurrentTime() / dur)
    }
    const handleFinish = () => {
      const { onProgress, onFinish } = callbacksRef.current
      onProgress?.(1)
      onFinish?.()
    }
    const handleError = () => callbacksRef.current.onError?.()
    const handlePlay = () => callbacksRef.current.onPlay?.()
    const handlePause = () => callbacksRef.current.onPause?.()

    wavesurfer.on('audioprocess', handleAudioProcess)
    wavesurfer.on('finish', handleFinish)
    wavesurfer.on('error', handleError)
    wavesurfer.on('play', handlePlay)
    wavesurfer.on('pause', handlePause)

    return () => {
      wavesurfer.un('audioprocess', handleAudioProcess)
      wavesurfer.un('finish', handleFinish)
      wavesurfer.un('error', handleError)
      wavesurfer.un('play', handlePlay)
      wavesurfer.un('pause', handlePause)
    }
  }, [wavesurfer])

  return (
    <div className="flex w-full items-center gap-2">
      <Button
        onClick={() => {
          if (!wavesurfer) return
          const media = wavesurfer.getMediaElement?.()
          if (media?.ended) {
            void wavesurfer.play(0)
            return
          }
          void wavesurfer.playPause()
        }}
        variant="tertiary"
        size="small"
        isDisabled={disabled}
        className="hover:text-green! flex w-[80px] shrink-0 items-center gap-1 hover:bg-transparent!"
      >
        {isPlaying ? (
          <>
            <Pause className="h-4 w-4 shrink-0" />
            <span>
              <Trans>Pause</Trans>
            </span>
          </>
        ) : (
          <>
            <Play className="h-4 w-4 shrink-0" />
            <span>
              <Trans>Play</Trans>
            </span>
          </>
        )}
      </Button>
      <div className="h-10 w-full flex-1" ref={playerRef} />
    </div>
  )
}
